import type {
  BlockRef,
  HeadingRef,
  MdLink,
  NoteParse,
  TagRef,
  WikiLink,
} from './types.js';
import { aliasesFrom, fmTagsFrom, parseFrontmatter } from './frontmatter.js';

/**
 * Obsidian-compatible markdown scanner. Regex/line based (no full AST) so it
 * is fast enough to run over the whole vault in the index worker on every
 * keystroke-debounce. Skips fenced code blocks and inline code spans.
 */

const FENCE = /^(\s*)(```|~~~)/;
const HEADING = /^(#{1,6})\s+(.*)$/;
const BLOCK_ID = /\s\^([A-Za-z0-9-]+)\s*$/;
// A tag begins after start-of-line or a non-word char; Obsidian allows
// letters, digits, _, -, / and requires at least one non-digit char.
const TAG = /(^|[\s([{'"‘“>])#([\p{L}\p{N}_/-]*[\p{L}_/-][\p{L}\p{N}_/-]*)/gu;
const WIKI = /(!?)\[\[([^\[\]\n]+)\]\]/g;
const MDLINK = /(!?)\[([^\]\n]*)\]\(([^)\n]+)\)/g;

function isInternalHref(href: string): boolean {
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(href)) return false; // has a scheme
  if (href.startsWith('#') || href.startsWith('//')) return false;
  return true;
}

/** Strip basic markdown formatting for heading anchor matching. */
export function headingAnchor(text: string): string {
  return text
    .replace(/\[\[([^\]|]*)\|([^\]]*)\]\]/g, '$2')
    .replace(/\[\[([^\]]*)\]\]/g, '$1')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[*_`~]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function parseWikiLinkInner(
  inner: string,
  embed: boolean,
  start: number,
  end: number,
  raw: string,
): WikiLink {
  let alias: string | undefined;
  const pipe = inner.indexOf('|');
  let ref = inner;
  if (pipe !== -1) {
    alias = inner.slice(pipe + 1).trim() || undefined;
    ref = inner.slice(0, pipe);
  }
  let blockId: string | undefined;
  let heading: string | undefined;
  const caret = ref.indexOf('#^');
  if (caret !== -1) {
    blockId = ref.slice(caret + 2).trim() || undefined;
    ref = ref.slice(0, caret);
  } else {
    const hash = ref.indexOf('#');
    if (hash !== -1) {
      heading = ref.slice(hash + 1).trim() || undefined;
      ref = ref.slice(0, hash);
    }
  }
  return {
    raw,
    target: ref.trim(),
    heading,
    blockId,
    alias,
    embed,
    start,
    end,
  };
}

/** Ranges of inline code spans within a single line. */
function codeSpans(line: string): Array<[number, number]> {
  const spans: Array<[number, number]> = [];
  let i = 0;
  while (i < line.length) {
    if (line[i] === '`') {
      let ticks = 1;
      while (line[i + ticks] === '`') ticks++;
      const closer = line.indexOf('`'.repeat(ticks), i + ticks);
      if (closer !== -1) {
        spans.push([i, closer + ticks]);
        i = closer + ticks;
        continue;
      }
    }
    i++;
  }
  return spans;
}

const inSpan = (spans: Array<[number, number]>, pos: number) =>
  spans.some(([a, b]) => pos >= a && pos < b);

export function parseNote(md: string): NoteParse {
  const fm = parseFrontmatter(md);
  const out: NoteParse = {
    frontmatter: fm.data,
    bodyStart: fm.bodyStart,
    aliases: aliasesFrom(fm.data),
    fmTags: fmTagsFrom(fm.data),
    wikilinks: [],
    mdLinks: [],
    tags: [],
    headings: [],
    blocks: [],
  };

  const body = md.slice(fm.bodyStart);
  const lines = body.split('\n');
  let offset = fm.bodyStart;
  let inFence = false;
  let fenceMarker = '';

  for (const line of lines) {
    const lineStart = offset;
    offset += line.length + 1;

    const fence = FENCE.exec(line);
    if (fence) {
      if (!inFence) {
        inFence = true;
        fenceMarker = fence[2];
        continue;
      } else if (line.trim().startsWith(fenceMarker)) {
        inFence = false;
        continue;
      }
    }
    if (inFence) continue;

    const spans = codeSpans(line);

    const h = HEADING.exec(line);
    if (h) {
      const text = h[2].replace(BLOCK_ID, '').trim();
      out.headings.push({
        level: h[1].length,
        text,
        anchor: headingAnchor(text),
        start: lineStart,
        end: lineStart + line.length,
      });
    }

    const b = BLOCK_ID.exec(line);
    if (b && b.index !== undefined) {
      out.blocks.push({
        id: b[1],
        start: lineStart + b.index + 1,
        end: lineStart + b.index + 1 + b[0].trim().length,
      });
    }

    WIKI.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = WIKI.exec(line))) {
      if (inSpan(spans, m.index)) continue;
      out.wikilinks.push(
        parseWikiLinkInner(
          m[2],
          m[1] === '!',
          lineStart + m.index,
          lineStart + m.index + m[0].length,
          m[0],
        ),
      );
    }

    MDLINK.lastIndex = 0;
    while ((m = MDLINK.exec(line))) {
      if (inSpan(spans, m.index)) continue;
      // skip if this [..](..) is actually inside a wikilink already captured
      const s = lineStart + m.index;
      if (out.wikilinks.some((w) => s >= w.start && s < w.end)) continue;
      const href = m[3].split(' ')[0]; // drop optional title
      out.mdLinks.push({
        href,
        text: m[2],
        internal: isInternalHref(href),
        embed: m[1] === '!',
        start: s,
        end: s + m[0].length,
      });
    }

    TAG.lastIndex = 0;
    while ((m = TAG.exec(line))) {
      const hashPos = m.index + m[1].length;
      if (inSpan(spans, hashPos)) continue;
      const tag = m[2].replace(/\/+$/, '');
      if (!tag) continue;
      // must not be purely numeric
      if (/^[\p{N}]+$/u.test(tag.replace(/\//g, ''))) continue;
      // skip heading lines' leading hashes are already excluded by regex
      // skip if inside a link target
      const pos = lineStart + hashPos;
      if (out.wikilinks.some((w) => pos >= w.start && pos < w.end)) continue;
      if (out.mdLinks.some((l) => pos >= l.start && pos < l.end)) continue;
      out.tags.push({ tag, start: pos, end: pos + 1 + tag.length });
    }
  }
  return out;
}
